/**
 * Mastery Decay Module
 *
 * Implements time-based mastery decay based on the exponential forgetting curve.
 * Without review, domain mastery decays toward zero.
 *
 * Based on: Ebbinghaus (1885) forgetting curve, Wixted & Ebbesen (1991)
 *
 * Half-life of ~23 days at default lambda (0.03):
 *   mastery_decayed = mastery_raw * e^(-0.03 * days)
 *   At 23 days: e^(-0.03 * 23) ≈ 0.50
 */

const DEFAULT_LAMBDA = 0.03; // decay rate (half-life ≈ 23 days)
const MS_PER_DAY = 86400000;

/**
 * Compute decayed mastery for a single domain
 *
 * @param {number} rawMastery - Peak mastery value [0, 1]
 * @param {number|Date|string} lastPracticedTs - When domain was last practiced
 * @param {number} lambda - Decay rate (default 0.03)
 * @returns {number} Decayed mastery value [0, 1]
 */
export function getDecayedMastery(rawMastery, lastPracticedTs, lambda = DEFAULT_LAMBDA) {
    if (!lastPracticedTs || rawMastery <= 0) return rawMastery;

    const lastTime = typeof lastPracticedTs === 'number'
        ? lastPracticedTs
        : new Date(lastPracticedTs).getTime();

    if (isNaN(lastTime)) return rawMastery;

    const daysSince = (Date.now() - lastTime) / MS_PER_DAY;
    if (daysSince <= 0) return rawMastery;

    return rawMastery * Math.exp(-lambda * daysSince);
}

/**
 * Compute decayed mastery for all domains
 *
 * @param {object} domainMastery - Raw mastery object { domain: number }
 * @param {object} lastPracticedByDomain - { domain: timestamp }
 * @param {number} lambda - Decay rate
 * @returns {object} { domain: { raw: number, decayed: number, daysSince: number } }
 */
export function computeAllDecayedMastery(domainMastery, lastPracticedByDomain = {}, lambda = DEFAULT_LAMBDA) {
    const result = {};

    Object.entries(domainMastery).forEach(([domain, raw]) => {
        const lastTs = lastPracticedByDomain[domain];
        const decayed = getDecayedMastery(raw, lastTs, lambda);
        const daysSince = lastTs
            ? (Date.now() - new Date(lastTs).getTime()) / MS_PER_DAY
            : null;

        result[domain] = {
            raw: raw,
            decayed: decayed,
            daysSince: daysSince ? Math.floor(daysSince) : null,
            decayPercent: raw > 0 ? ((raw - decayed) / raw) * 100 : 0
        };
    });

    return result;
}

/**
 * Generate study recommendations based on decay urgency
 *
 * @param {object} decayedMastery - Output from computeAllDecayedMastery
 * @returns {Array} Sorted recommendations [{ domain, urgency, message, decayed, raw }]
 */
export function getStudyRecommendations(decayedMastery) {
    const recommendations = [];

    Object.entries(decayedMastery).forEach(([domain, data]) => {
        const { raw, decayed, decayPercent, daysSince } = data;

        if (raw <= 0.05) {
            // Never really studied — not a decay issue, just unstudied
            recommendations.push({
                domain,
                urgency: 'new',
                message: `Start studying ${domain} to build foundational knowledge.`,
                decayed,
                raw
            });
        } else if (decayPercent > 30) {
            recommendations.push({
                domain,
                urgency: 'high',
                message: `${domain} has decayed significantly (${Math.round(decayPercent)}% lost). Review now to recover.`,
                decayed,
                raw
            });
        } else if (decayPercent > 15) {
            recommendations.push({
                domain,
                urgency: 'medium',
                message: `${domain} is starting to fade. Review soon to maintain mastery.`,
                decayed,
                raw
            });
        }
    });

    // Sort: high urgency first, then by lowest decayed mastery
    const urgencyOrder = { high: 0, medium: 1, new: 2 };
    recommendations.sort((a, b) => {
        const urgDiff = (urgencyOrder[a.urgency] ?? 3) - (urgencyOrder[b.urgency] ?? 3);
        if (urgDiff !== 0) return urgDiff;
        return a.decayed - b.decayed;
    });

    return recommendations;
}

/**
 * Apply decay before updating mastery on a new response
 * Decays first, then adds the response delta on top
 *
 * @param {number} rawMastery - Current stored raw mastery
 * @param {number|Date|string} lastPracticedTs - When this domain was last practiced
 * @param {boolean} correct - Whether the response was correct
 * @param {number} difficulty - Item difficulty (author scale, 0-0.9)
 * @param {number} lambda - Decay rate
 * @returns {{ newMastery: number, newLastPracticed: number }}
 */
export function updateMasteryWithDecay(rawMastery, lastPracticedTs, correct, difficulty, lambda = DEFAULT_LAMBDA) {
    // First, apply decay since last practice
    const decayed = getDecayedMastery(rawMastery, lastPracticedTs, lambda);

    // Then apply response delta on decayed value
    const delta = correct ? 0.1 * difficulty : -0.05 * difficulty;
    const newMastery = Math.max(0, Math.min(1, decayed + delta));

    return {
        newMastery,
        newLastPracticed: Date.now()
    };
}
